import { Grid, Hidden } from '@material-ui/core';
import { Form, Formik, FormikProps } from 'formik';
import React, { useContext, useState } from 'react';
import Banner from '@cpg-its/ads.containers.banner';
import AuthContext from '@cpg-its/ads.auth.auth-context';
import { useSelector } from 'react-redux';
import { FormField } from '../../../../../../global/components/form-element/form-field';
import { FormWrapper } from '../../../../../../global/components/form-wrapper';
import { GuidedProcessSupportBlock } from '../../../../../../global/components/guided-process-support-block';
import { DividerSection } from '../../../../../../global/components/layout/divider-section';
import { ContentSpacer } from '../../../../../../global/components/layout/generic-content-spacer';
import { MainContent } from '../../../../../../global/components/layout/main-content-container';
import { PageContainer } from '../../../../../../global/components/layout/page-container';
import { PageHeader } from '../../../../../../global/components/layout/page-header';
import { SideBarContent } from '../../../../../../global/components/layout/sidebar-content';
import { useApiResponseError } from '../../../../../../global/hooks/useApiError';
import { useGpsType } from '../../../../../../global/hooks/useGpsType';
import { Institution } from '../../../../../../global/models/institution-model';
import { TcsmStates } from '../../../../../../root/tcsm-model';
import AdoptingInstitutionView from '../display-components/adopting-institution-view';
import { DbPlanFormModel } from '../models/plan-adoption-form-models';
import { WysiwygWithReactLink } from '../../../../../../global/components/wysiwyg-with-react-link';
import { PlanSummary } from '../../institution-model';
import { buttonStyles } from '../../../../../../global/constants/theme/styles/components/buttonstyles';
import DocusignView from '../../../docusign/docusign-view';
import { DocuSignRequest, DocuSignRequestOptions } from '../../../docusign/models/docusign-models';
import { Person } from '../../../../../../global/models/person-model';
import { DemographicAppKeys } from '../../../../../../global/models/demographics-model';
import { LayDbPlanAdoptionWizard } from '../wizards/lay-db-plan-adoption-wizard';
import { FormGroup } from '../../../../../../global/components/layout/form-group';
import { YesNoOptions } from '../../../../../../global/constants/lists';
import { YesNoIndicators } from '../../../../../../global/models/api-model';

interface Props {
	wizard: LayDbPlanAdoptionWizard;
}

function DbPlanAdoptionForm(props: Props) {

	const { wizard } = props;
	const { accessToken, decodedToken } = useContext(AuthContext) as any;
	const content = wizard?.getFormContentModel();
	const gpsItem = useGpsType(content?.gpsType);
	const apiErrors = useApiResponseError();
	const currentInstitution = useSelector<TcsmStates, Institution>(state => state?.institutionState?.currentInstitution);
	const currentPlans = useSelector<TcsmStates, PlanSummary>(state => state?.institutionState?.planSummary);
	const needsIndemnify = wizard?.needIndemnify(currentPlans);
	const hasRsvp = wizard?.hasRsvp(currentPlans);
	const buttonClasses = buttonStyles();
	const [hideQuestion, setHideQuestion] = useState(false);
	const [indemnify, setIndemnify] = useState(needsIndemnify);
	const [rsvpComply, setRsvpComply] = useState(false);
	const labelSettings = wizard?.epiContent?.labelSettingState?.epiContent;
	const adoptionDateOptions = wizard?.getAdoptionDateOptions();
	const [showDocusignView, setShowDocusignView] = useState(false);
	const person = useSelector<TcsmStates, Person>(state => state?.personalInfo[DemographicAppKeys.myCPG].currentPerson);
	const onComplete = () => {
		setIndemnify(false);
		setShowDocusignView(false);
		setHideQuestion(true);
	};

	const docuSignRequest: DocuSignRequest = indemnify ? wizard?.prepareIndemnifyDocuSignRequest(person, currentInstitution, accessToken, decodedToken?.iat) : wizard?.prepareDocuSignRequest(person, currentInstitution, accessToken, decodedToken?.iat, needsIndemnify);
	const docuSignOptions: DocuSignRequestOptions = indemnify ? wizard.getIndemnifyDocusignOption(docuSignRequest, setShowDocusignView, onComplete, currentInstitution) : wizard?.getDocusignOption(docuSignRequest, setShowDocusignView, currentInstitution, needsIndemnify);
	function renderDocusignSignView() {
		if (!showDocusignView) return null;

		return <DocusignView options={docuSignOptions} />;
	}

	function complyChanged(e) {
		const { value } = e.target;
		if (value === YesNoIndicators.Yes) {
			setRsvpComply(true);
			setIndemnify(true);

		}
		if (value === YesNoIndicators.No) {
			setRsvpComply(false);
			setIndemnify(false);

		}
	}

	function renderComplyEpiscopalChurchLayPensionSystemQuestion(formProps: FormikProps<DbPlanFormModel>) {

		if (!hasRsvp || hideQuestion) return null;

		return (
			<FormGroup>
				<FormField
					name='complyEpiscopalChurchLayPensionSystem'
					label={content?.complyEpiscopalChurchLayPensionSystemQuestion}
					component="radioGroup"
					options={YesNoOptions}
					touched={formProps.touched}
					errors={formProps.errors}
					onChange={(e) => complyChanged(e)}
					required
				/>
			</FormGroup>
		);
	}

	return (
		<PageContainer>
			<MainContent>
				<Formik
					initialValues={wizard?.getFormInitialValue()}
					onSubmit={
						(values) => wizard?.onSubmit(
							{
								values,
								clientId: currentInstitution?.org_client_nbr,
								toggleDocusign: setShowDocusignView,
							})()
					}
					enableReinitialize
					initialErrors={apiErrors}
					validationSchema={wizard?.getFormValidationSchema()}
				>
					{(formProps: FormikProps<DbPlanFormModel>) => (
						<>
							<Form>
								<Grid>
									<FormWrapper
										formikProps={formProps}
										onCancel={null}
										submitText=''
										cancelText=''
										hideButtons
									>
										{
											needsIndemnify && !indemnify && formProps?.values?.complyEpiscopalChurchLayPensionSystem !== YesNoIndicators.No &&
											<ContentSpacer>
												<Banner
													banner={{
														type: 'success',
														label: <WysiwygWithReactLink content={content?.indemnifySuccessMessage} />,
													}}
												/>

											</ContentSpacer>
										}
										<PageHeader
											headerText={content?.pageTitle}
											description={needsIndemnify && indemnify ? null : content?.PageDescription}
										/>
										<Hidden mdUp>
											<SideBarContent>
												<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
											</SideBarContent>
										</Hidden>
										{
											renderComplyEpiscopalChurchLayPensionSystemQuestion(formProps)
										}
										{
											((needsIndemnify && indemnify) || formProps?.values?.complyEpiscopalChurchLayPensionSystem === YesNoIndicators.No) &&
											<DividerSection disabledOverlay={hasRsvp && !rsvpComply} removeDivider >
												<ContentSpacer>
													<Banner
														banner={{
															type: 'error',
															label: wizard?.getIndemnifyWarningTitle(currentPlans),
															description: <>
																<WysiwygWithReactLink content={hasRsvp ? content?.indemnifyWarningRsvpMessage : content?.indemnifyWarningMessage} />
																<ContentSpacer>
																	<button className={buttonClasses.primaryButton} type='button' onClick={() => setShowDocusignView(true)}>{content?.indemnifyButtonText}</button>
																</ContentSpacer>
																<p>{content?.indemnifyButtonHelper}</p>
															</>,
														}}
													/>

												</ContentSpacer>
											</DividerSection>
										}

										<DividerSection disabledOverlay={indemnify} sectionText={content?.planAdoptionDetailTitle} removeDivider >
											<AdoptingInstitutionView institution={currentInstitution} clientNumberLabel={content?.clientIdLabel} sectionTitle={content?.adoptingInstitutionLabel} />
											<ContentSpacer>
												<FormField
													name='adoption_date'
													label={content?.adoptionDateLabel}
													component="input"
													options={adoptionDateOptions}
													touched={formProps.touched}
													errors={formProps.errors}
													required
													placeholder={labelSettings?.selectDefaultText}
													select
													autoWidth
												/>

											</ContentSpacer>

											{
												!showDocusignView &&
												<ContentSpacer>
													<button className='btn-primary' type='button' onClick={() => formProps.submitForm()}>
														<span>{content?.signHereLabel}</span>
													</button>
												</ContentSpacer>
											}
											<ContentSpacer>
												<WysiwygWithReactLink content={content?.signHereDescription} />
											</ContentSpacer>
										</DividerSection>
										{renderDocusignSignView()}
									</FormWrapper>
								</Grid>
							</Form>
						</>
					)}
				</Formik>
			</MainContent>
			<Hidden smDown>
				<SideBarContent>
					<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
				</SideBarContent>
			</Hidden>
		</PageContainer>
	);

}

export default DbPlanAdoptionForm;